import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { X, CalendarDays } from 'lucide-react';

const predefinedColors = [
  '#ffffff', '#e5e7eb', '#94a3b8', '#64748b', '#ef4444',
  '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e',
  '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6',
];

const CreateCalendarModal = ({ isOpen, onClose, onCreate, onUpdate, mode = 'create', initialCalendar }) => {
  const { t } = useTranslation('calendar');
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3b82f6');
  const [description, setDescription] = useState('');
  const [showSpecialDays, setShowSpecialDays] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'edit' && initialCalendar) {
      setName(initialCalendar.name || '');
      setColor(initialCalendar.color || '#3b82f6');
      setDescription(initialCalendar.description || '');
      setShowSpecialDays(initialCalendar.showSpecialDays || false);
    } else {
      setName(''); setColor('#3b82f6'); setDescription(''); setShowSpecialDays(false);
    }
  }, [isOpen, mode, initialCalendar]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const payload = { name: name.trim(), color, description: description.trim() };
    if (mode === 'edit') await onUpdate?.(payload);
    else await onCreate?.(payload);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000]"
            onClick={onClose}
          />

          <motion.div
            className="fixed inset-0 flex items-center justify-center z-[100001] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 8 }}
              transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
              className="w-full max-w-sm bg-[var(--color-bg-base)] rounded-2xl border border-white/[0.08] shadow-[0_32px_80px_rgba(0,0,0,0.7)] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-start justify-between p-6 pb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#2563eb]/10 border border-[#3d8ef0]/20 flex items-center justify-center flex-shrink-0">
                    <CalendarDays className="w-5 h-5 text-[#3d8ef0]" strokeWidth={2} />
                  </div>
                  <h3 className="text-base font-semibold text-[var(--color-text-primary)]">
                    {mode === 'edit' ? t('createCalendarModal.editTitle') : t('createCalendarModal.createTitle')}
                  </h3>
                </div>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg text-[#4a6380] hover:text-[var(--color-text-secondary)] hover:bg-white/[0.06] transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Form */}
              <div className="px-6 pb-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Takvim Adı */}
                  <div>
                    <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-2">
                      {t('createCalendarModal.nameLabel')} <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t('createCalendarModal.namePlaceholder')}
                      autoFocus
                      required
                      className="w-full px-3.5 py-2.5 bg-[var(--color-bg-muted)] border border-white/[0.07] rounded-xl text-sm text-[var(--color-text-primary)] placeholder-[#4a6380] focus:outline-none focus:border-[#3d8ef0]/50 focus:ring-1 focus:ring-[#3d8ef0]/30 transition-all"
                    />
                  </div>

                  {/* Açıklama */}
                  <div>
                    <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-2">
                      {t('createCalendarModal.descriptionLabel')}
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t('createCalendarModal.descriptionPlaceholder')}
                      rows={3}
                      className="w-full px-3.5 py-2.5 bg-[var(--color-bg-muted)] border border-white/[0.07] rounded-xl text-sm text-[var(--color-text-primary)] placeholder-[#4a6380] focus:outline-none focus:border-[#3d8ef0]/50 focus:ring-1 focus:ring-[#3d8ef0]/30 transition-all resize-none"
                    />
                  </div>

                  {/* Renk */}
                  <div>
                    <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-2">{t('createCalendarModal.colorLabel')}</label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 flex flex-wrap gap-1.5">
                        {predefinedColors.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setColor(c)}
                            className="w-7 h-7 rounded-lg transition-all"
                            style={{
                              backgroundColor: c,
                              outline: color === c ? `2px solid ${c}` : '2px solid transparent',
                              outlineOffset: '2px',
                              opacity: color === c ? 1 : 0.75,
                            }}
                          />
                        ))}
                      </div>
                      <input
                        type="color"
                        value={color}
                        onChange={(e) => setColor(e.target.value)}
                        className="w-10 h-8 rounded-lg cursor-pointer border border-white/[0.07] bg-[var(--color-bg-muted)]"
                        title={t('createCalendarModal.customColorTitle')}
                      />
                    </div>
                  </div>

                  {/* Özel Günler */}
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <div className="relative flex-shrink-0 mt-0.5">
                      <input
                        type="checkbox"
                        checked={showSpecialDays}
                        onChange={(e) => setShowSpecialDays(e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                        showSpecialDays ? 'bg-[#2563eb] border-[#3d8ef0]' : 'bg-[var(--color-bg-muted)] border-white/20'
                      }`}>
                        {showSpecialDays && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-[var(--color-text-secondary)] group-hover:text-[var(--color-text-primary)] transition-colors">
                        {t('createCalendarModal.showSpecialDaysLabel')}
                      </span>
                      <p className="text-xs text-[#4a6380] mt-0.5">
                        {t('createCalendarModal.showSpecialDaysHint')}
                      </p>
                    </div>
                  </label>

                  {/* Buttons */}
                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={onClose}
                      className="flex-1 py-2.5 rounded-xl text-sm font-medium text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] hover:bg-white/[0.05] border border-white/[0.07] transition-colors"
                    >
                      {t('createCalendarModal.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={!name.trim()}
                      className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-[#2563eb] hover:bg-[#3d8ef0] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {mode === 'edit' ? t('createCalendarModal.save') : t('createCalendarModal.create')}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CreateCalendarModal;
